import React, { FC, MouseEventHandler, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { useMutation } from '@apollo/client';

import Button from 'vibo-ui/Button';
import WithEventRole from 'components/user/WithEventRole';
import StopPropagationWrapper from 'components/common/StopPropagationWrapper';

import { onError } from 'graphql/helpers';
import { AllTimelineGqlOptions, useTimeline } from 'components/events/Timeline/TimelineContext';

import { IconmoonFont } from 'vibo-ui/Icon';
import { EventUserType } from 'types/enums';
import { NotifyUpdatesButtonProps } from './interfaces';
import { useEvent } from 'components/context/EventContext';

const NotifyUpdatesButton: FC<NotifyUpdatesButtonProps> = ({ defaultActive, queryKey, name }) => {
  const [isActive, setIsActive] = useState(false);

  const {
    variables,
    sections: { mutations },
  } = useTimeline<AllTimelineGqlOptions>();
  const { isPublicTemplate, canEdit } = useEvent();

  const [updateSection] = useMutation(mutations.updateSection.mutation, {
    onError,
  });

  const toggleActive: MouseEventHandler<HTMLButtonElement> = debounce(() => {
    setIsActive(prev => {
      updateSection({
        variables: {
          ...variables,
          payload: {
            settings: {
              [queryKey]: !prev,
            },
          },
        },
      });

      return !prev;
    });
  }, 500);

  useEffect(() => {
    setIsActive(defaultActive);
  }, [defaultActive]);

  return (
    <WithEventRole roles={[EventUserType.dj]}>
      <StopPropagationWrapper>
        <Button
          onClick={toggleActive}
          disabled={isPublicTemplate && !canEdit}
          tooltipProps={{
            overlay: isActive
              ? `Don’t notify me about ${name.toLocaleLowerCase()} updates`
              : `Notify me about ${name.toLocaleLowerCase()} updates`,
            fillOverlay: true,
            placement: 'top',
            mouseEnterDelay: 1,
          }}
          prefixIcon={IconmoonFont[isActive ? 'notifyMeBellFilled-16' : 'notifyMeBellOutlined-16']}
          shape="round"
          size="lg"
        />
      </StopPropagationWrapper>
    </WithEventRole>
  );
};

export default NotifyUpdatesButton;
